import React from 'react';
import {Table, Typography} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useTranslation} from 'react-i18next';
import {PodExemptReportType} from '../../graphql/podExempt/types';
import PodExemptStatusTag from './PodExemptStatusTag';

interface Props {
  data: PodExemptReportType[];
  loading: boolean;
  /** 行尾操作列（各端不同：DSP=查看+撤回、审核队列=审核） */
  renderActions?: (record: PodExemptReportType) => React.ReactNode;
  onRowClick?: (record: PodExemptReportType) => void;
  /** 字典 code → 展示名（原因/地址类型），缺失时回退 code */
  dictNameOf?: (code: string) => string;
  /**
   * review = 审核队列（对齐审核原型）：DSP/提报人合并列 + 7天单量 + 材料计数；
   * default = DSP 端现状列序。
   */
  variant?: 'default' | 'review';
}

/**
 * 上报单列表（对齐原型列序）：
 * ID / 上报维度 / 地址÷围栏（双行）/ 地址类型 / 无法拍摄原因 / DSP(/提报人) / [7天单量 / 材料] / 提交时间 / 状态 / 操作。
 * 分页由父级 TablePaginationBar 承担，本组件只渲染当前页。
 */
const PodExemptReportTable: React.FC<Props> = ({
  data,
  loading,
  renderActions,
  onRowClick,
  dictNameOf,
  variant = 'default',
}) => {
  const {t} = useTranslation();
  const nameOf = dictNameOf ?? ((code: string) => code);
  const isReview = variant === 'review';

  const columns: ColumnsType<PodExemptReportType> = [
    {
      title: 'ID',
      dataIndex: 'reportNo',
      width: 110,
      render: (v: string, record) => (
        <Typography.Link onClick={() => onRowClick?.(record)}>{v}</Typography.Link>
      ),
    },
    {
      title: t('podExempt.field.scopeType'),
      dataIndex: 'scopeType',
      width: 90,
      render: (v: string) =>
        v === 'FENCE' ? t('podExempt.scope.fence') : t('podExempt.scope.single'),
    },
    {
      title: t('podExempt.field.addressOrFence'),
      dataIndex: 'displayAddress',
      width: 260,
      render: (_, record) => {
        const metaParts = [record.zipcode, record.warehouseName ?? record.warehouseCode].filter(Boolean);
        if (record.scopeType === 'FENCE') {
          // 地址数在提交后由后端异步估算回填（秒级），生效后每日刷新，各状态下有值即展示；
          // 生效围栏仍为 null 说明估算被丢弃或失败，显示「统计中」等夜间刷新；待审围栏为 null 则不展示该段
          if (record.addressCount != null) {
            metaParts.push(t('podExempt.field.addressCountUnit', {count: record.addressCount}));
          } else if (record.status === 'EFFECTIVE') {
            metaParts.push(t('podExempt.field.addressCounting'));
          }
        }
        return (
          <div className="leading-normal">
            <div>{record.displayAddress ?? '-'}</div>
            <div className="text-xs text-gray-400 mt-0.5">{metaParts.join(' · ')}</div>
          </div>
        );
      },
    },
    {
      title: t('podExempt.field.addressType'),
      dataIndex: 'addressType',
      width: 130,
      render: (v: string | null) => (v ? nameOf(v) : '-'),
    },
    {
      title: t('podExempt.field.reasons'),
      dataIndex: 'reasonCodes',
      width: 200,
      render: (codes: string[]) => (codes ?? []).map(nameOf).join(', ') || '-',
    },
    ...(isReview
      ? ([
          {
            title: t('podExempt.review.dspReporter'),
            key: 'dspReporter',
            width: 140,
            render: (_: unknown, record: PodExemptReportType) => (
              <div className="leading-normal">
                <div>{record.dspName ?? '-'}</div>
                <div className="text-xs text-gray-400 mt-0.5">{record.creatorName ?? '-'}</div>
              </div>
            ),
          },
          {
            title: t('podExempt.review.orders7d'),
            dataIndex: 'recent7dOrderCount',
            width: 90,
            // 数据源未接入（口径已拍板：字段留着后续对接），占位展示
            render: (v: number | null) => (v != null ? v : '—'),
          },
          {
            title: t('podExempt.review.materialsCol'),
            key: 'materials',
            width: 170,
            render: (_: unknown, record: PodExemptReportType) => (
              <span className={record.materialIncomplete ? 'text-red-500' : undefined}>
                {t('podExempt.review.materialsValue', {
                  photo: record.photoCount ?? 0,
                  video: record.videoCount ?? 0,
                  file: record.fileCount ?? 0,
                })}
              </span>
            ),
          },
        ] as ColumnsType<PodExemptReportType>)
      : ([
          {
            title: 'DSP',
            dataIndex: 'dspName',
            width: 130,
            render: (v: string | null) => v ?? '-',
          },
          {
            title: t('podExempt.field.creator'),
            dataIndex: 'creatorName',
            width: 110,
            render: (v: string | null) => v ?? '-',
          },
        ] as ColumnsType<PodExemptReportType>)),
    {
      title: t(isReview ? 'podExempt.review.submitTime' : 'podExempt.field.createdAt'),
      dataIndex: 'createdAt',
      width: 170,
      render: (v: string | null) => (v ? new Date(v).toLocaleString() : '-'),
    },
    {
      title: t('podExempt.field.status'),
      dataIndex: 'status',
      width: 100,
      render: (_, record) => <PodExemptStatusTag status={record.status} />,
    },
  ];

  if (renderActions) {
    columns.push({
      title: t('podExempt.common.actions'),
      key: 'actions',
      fixed: 'right',
      width: isReview ? 90 : 140,
      render: (_, record) => renderActions(record),
    });
  }

  return (
    <Table<PodExemptReportType>
      rowKey="id"
      size="small"
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={false}
      scroll={{x: 'max-content'}}
    />
  );
};

export default PodExemptReportTable;
